import { useEffect, useMemo, useState } from 'react';

type PermissionMap = Record<string, Record<string, boolean>>;

const DEFAULT_STORAGE_KEY = 'permissions';

/**
 * Usage:
 * const canCreateCase = useHasPermission("Cases", "Create");
 * const canCreateSettlement = useHasPermission("Payments", "Create Settlement");
 */

export function hasStoredRoles(storageKey: string = DEFAULT_STORAGE_KEY): boolean {
  const perms = readPermissions(storageKey);
  return Object.keys(perms).length > 0;
}

function readPermissions(storageKey: string): PermissionMap {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}


export function useHasPermission(
  moduleName: string,
  action: string,
  storageKey: string = DEFAULT_STORAGE_KEY
): boolean {
  const [permissions, setPermissions] = useState<PermissionMap>(() =>
    readPermissions(storageKey)
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === storageKey) {
        setPermissions(readPermissions(storageKey));
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  return useMemo(() => {
    return Boolean(permissions?.[moduleName]?.[action]);
  }, [permissions, moduleName, action]);
}